import { Box, DebouncedTextField, Flex, Image, Menu, MenuOption, Spinner, Text } from 'components'
import { useCallback, useEffect, useState } from 'react'
import { Font, FontPaginationResponse } from 'types'
import { getMomentifyFonts, getWorkspaceFontPreview } from 'utils'
import { MagnifyingGlassIcon } from './icons'
import { useWorkspace } from './providers'

export default function FontPicker() {
  const { workspace, updateWorkspace } = useWorkspace()
  const [fonts, setFonts] = useState<Font[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [activeFontPreviewUrl, setActiveFontPreviewUrl] = useState<string>('')
  const [open, setOpen] = useState(false)

  const activeFontFamily = workspace?.branding?.fontFamily || 'Inter'
  const pageSize = 20

  const loadFonts = useCallback(
    async (page: number, append = false, search = '') => {
      try {
        if (append) setLoadingMore(true)
        else setLoading(true)

        const response = await getMomentifyFonts({ page, pageSize, search })
        const data = response.data as FontPaginationResponse

        setFonts(prev => (append ? [...prev, ...data.fonts] : data.fonts))
        setHasMore(data.hasMore)
        setTotalCount(data.totalCount)
        setCurrentPage(page)
      } catch (e) {
        console.error('Error loading fonts:', e)
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [pageSize]
  )

  useEffect(() => {
    if (!open) return
    loadFonts(1, false, searchTerm)
  }, [open, searchTerm, loadFonts])

  useEffect(() => {
    const fetchPreview = async () => {
      if (activeFontFamily) {
        setLoading(true)
        const previewUrl = await getWorkspaceFontPreview({ workspaceId: workspace?.id! })
        setActiveFontPreviewUrl(previewUrl.data)
        setLoading(false)
      }
    }
    fetchPreview()
  }, [workspace?.id, activeFontFamily])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget
    if (
      target.scrollHeight - target.scrollTop <= target.clientHeight + 50 &&
      hasMore &&
      !loadingMore
    )
      loadFonts(currentPage + 1, true, searchTerm)
  }

  const handleFontSelect = (family: string) => {
    updateWorkspace({
      branding: { ...workspace?.branding, fontFamily: desanitizeFontName(family) },
    })
  }

  const options: MenuOption<string>[] = [
    {
      type: 'custom',
      label: (
        <DebouncedTextField
          width="100%"
          mb={0.5}
          boxShadow="0px 1px 4px 0px rgba(0, 0, 0, 0.06)"
          onChange={setSearchTerm}
          leftElement={<MagnifyingGlassIcon color="rgba(23, 23, 23, 0.6)" />}
          placeholder="Search..."
          value={searchTerm}
          controlled
          backdropFilter="blur(14px)"
          borderRadius="10px"
          borderWidth={0}
        />
      ),
    },
    {
      type: 'group',
      overflow: 'auto',
      maxHeight: '300px',
      onScroll: handleScroll,
      options: fonts.map(font => ({
        type: 'item' as const,
        value: font.family,
        label: <Image key={font.family} fit="fill" h="30px" src={font.previewUrl} />,
        onClick: () => handleFontSelect(font.family),
      })),
    },
  ]

  if (loading && fonts.length === 0) {
    options.push({
      type: 'item' as const,
      value: 'loading',
      label: (
        <Box p={4} textAlign="center">
          <Spinner size="sm" />
          <Text fontSize="sm" color="gray.500" mt={2}>
            Loading fonts...
          </Text>
        </Box>
      ),
    })
  }

  options.push({
    type: 'custom',
    label: (
      <Flex p={1} align="center" gap={2} justify="center">
        {loadingMore ? (
          <>
            <Spinner size="sm" />
            <Text fontSize="xs" color="gray.500" mt={2}>
              Loading more fonts...
            </Text>
          </>
        ) : !hasMore && fonts.length > 0 ? (
          <Text fontSize="xs" color="gray.500">
            {totalCount} font{fonts.length > 1 ? 's' : ''} loaded
          </Text>
        ) : null}
      </Flex>
    ),
  })

  return (
    <Menu
      label="Font Family"
      loading={loading}
      value={activeFontFamily}
      width="250px"
      buttonLabel={<Image key={activeFontFamily} fit="fill" h="30px" src={activeFontPreviewUrl} />}
      menuProps={{
        onOpenChange: v => setOpen(v.open),
        lazyMount: true,
      }}
      contentProps={{
        p: 2,
      }}
      options={options}
    />
  )
}

function desanitizeFontName(sanitized: string): string {
  return sanitized.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}
